import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Code2, BookOpen } from 'lucide-react';

// ---------------------------------------------------------------------------
// Packet parsing utilities
// ---------------------------------------------------------------------------

function signedInt16(unsignedInt: number): number {
  const buffer = new ArrayBuffer(2);
  const view = new DataView(buffer);
  view.setUint16(0, unsignedInt);
  return view.getInt16(0);
}

function convertValue(sensorType: number, value: number): number {
  const originalSensorType = sensorType;
  if (originalSensorType >= 0xe8 && originalSensorType <= 0xeb) {
    return value * (10 ** (0xeb - originalSensorType));
  }
  sensorType = sensorType & 0xf8;
  if (sensorType === 0x60 || sensorType === 0x70 || sensorType === 0xc0) return value;
  if (sensorType === 0x18) return Math.round(100 * (value / 100 - 200)) / 1000;
  if (sensorType === 0x20 && originalSensorType !== 0x20) return signedInt16(value) / 10;
  if (sensorType === 0x20) return signedInt16(value) / 10;
  return Math.round(100 * (value / 100 - 200)) / 100;
}

function verifyChecksum(pkt: Uint8Array): boolean {
  const toCheck = pkt[pkt.length - 1];
  let checksum = 0;
  for (let i = 0; i < pkt.length - 1; i++) {
    checksum += pkt[i];
  }
  checksum = 255 - (checksum & 0xff);
  return checksum === toCheck;
}

function bytesToHex(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

function convertIntToFloat(value: number): number {
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  if (value < 0) view.setInt32(0, value);
  else view.setUint32(0, value);
  return view.getFloat32(0);
}

function shouldPatchLegacyFloat(key: string, value: CborValue): value is number {
  return (
    typeof value === 'number'
    && Number.isInteger(value)
    && (key.startsWith('offset_') || key.startsWith('alert_'))
    && (value > 10000 || value < -10000)
  );
}

function formatCborValue(value: CborValue): string | number | boolean {
  if (value instanceof Uint8Array) return bytesToHex(value);
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    return JSON.stringify(toJsonSafe(value));
  }
  if (typeof value === 'number' && Number.isNaN(value)) return 'NaN';
  if (value === null || value === undefined) return String(value);
  return value;
}

function toJsonSafe(value: CborValue): unknown {
  if (value instanceof Uint8Array) return bytesToHex(value);
  if (typeof value === 'number' && Number.isNaN(value)) return 'NaN';
  if (Array.isArray(value)) return value.map(toJsonSafe);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, toJsonSafe(entry)]));
  }
  return value;
}

function cborType(value: CborValue): string {
  if (value instanceof Uint8Array) return 'bytes';
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  return typeof value;
}

function productIdFromConfig(config: Record<string, CborValue>): string {
  const mac = config.mac;
  if (typeof mac === 'number' && Number.isFinite(mac)) {
    return Math.trunc(mac).toString(16).toUpperCase().padStart(12, '0');
  }
  if (typeof mac === 'string') {
    const normalized = mac.replace(/[^a-fA-F0-9]/g, '');
    if (normalized) return normalized.toUpperCase().padStart(12, '0');
  }
  return '—';
}

class CborReader {
  private readonly textDecoder = new TextDecoder();

  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {}

  readValue(): CborValue {
    const initial = this.readUint8();
    const major = initial >> 5;
    const additional = initial & 0x1f;

    if (major === 0) return this.readLength(additional);
    if (major === 1) return -1 - this.readLength(additional);
    if (major === 2) return this.readBytes(this.readLength(additional));
    if (major === 3) return this.textDecoder.decode(this.readBytes(this.readLength(additional)));
    if (major === 4) {
      const length = this.readLength(additional);
      return Array.from({ length }, () => this.readValue());
    }
    if (major === 5) {
      const length = this.readLength(additional);
      const result: Record<string, CborValue> = {};
      for (let i = 0; i < length; i++) {
        const key = this.readValue();
        if (typeof key !== 'string') throw new Error('CBOR config map keys must be text strings');
        result[key] = this.readValue();
      }
      return result;
    }
    if (major === 6) {
      this.readLength(additional);
      return this.readValue();
    }
    if (major === 7) return this.readSimple(additional);

    throw new Error(`Unsupported CBOR major type ${major}`);
  }

  assertDone(): void {
    if (this.offset !== this.bytes.length) {
      throw new Error(`Unexpected trailing CBOR bytes: ${this.bytes.length - this.offset}`);
    }
  }

  private readSimple(additional: number): CborValue {
    if (additional === 20) return false;
    if (additional === 21) return true;
    if (additional === 22 || additional === 23) return null;
    if (additional === 24) return this.readUint8();
    if (additional === 25) return this.readFloat16();
    if (additional === 26) {
      const value = this.readBytes(4);
      return new DataView(value.buffer, value.byteOffset, value.byteLength).getFloat32(0);
    }
    if (additional === 27) {
      const value = this.readBytes(8);
      return new DataView(value.buffer, value.byteOffset, value.byteLength).getFloat64(0);
    }
    throw new Error(`Unsupported CBOR simple value ${additional}`);
  }

  private readFloat16(): number {
    const half = this.readUint16();
    const sign = (half & 0x8000) ? -1 : 1;
    const exponent = (half & 0x7c00) >> 10;
    const fraction = half & 0x03ff;

    if (exponent === 0) return sign * 2 ** -14 * (fraction / 2 ** 10);
    if (exponent === 0x1f) return fraction ? NaN : sign * Infinity;
    return sign * 2 ** (exponent - 15) * (1 + fraction / 2 ** 10);
  }

  private readLength(additional: number): number {
    if (additional < 24) return additional;
    if (additional === 24) return this.readUint8();
    if (additional === 25) return this.readUint16();
    if (additional === 26) return this.readUint32();
    if (additional === 27) return this.readUint64();
    throw new Error('Indefinite-length CBOR is not supported');
  }

  private readUint8(): number {
    if (this.offset >= this.bytes.length) throw new Error('Unexpected end of CBOR data');
    return this.bytes[this.offset++];
  }

  private readUint16(): number {
    const value = this.readBytes(2);
    return new DataView(value.buffer, value.byteOffset, value.byteLength).getUint16(0);
  }

  private readUint32(): number {
    const value = this.readBytes(4);
    return new DataView(value.buffer, value.byteOffset, value.byteLength).getUint32(0);
  }

  private readUint64(): number {
    const high = this.readUint32();
    const low = this.readUint32();
    const value = high * 2 ** 32 + low;
    if (!Number.isSafeInteger(value)) throw new Error('CBOR integer exceeds JavaScript safe integer range');
    return value;
  }

  private readBytes(length: number): Uint8Array {
    if (this.offset + length > this.bytes.length) throw new Error('Unexpected end of CBOR data');
    const value = this.bytes.slice(this.offset, this.offset + length);
    this.offset += length;
    return value;
  }
}

function decodeCborConfig(bytes: Uint8Array): Record<string, CborValue> {
  const reader = new CborReader(bytes);
  const decoded = reader.readValue();
  reader.assertDone();

  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded) || decoded instanceof Uint8Array) {
    throw new Error('CBOR config payload must decode to a map');
  }

  const config = { ...decoded };
  Object.entries(config).forEach(([key, value]) => {
    if (shouldPatchLegacyFloat(key, value)) {
      config[key] = convertIntToFloat(value);
    }
  });

  return config;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CborValue = string | number | boolean | null | Uint8Array | CborValue[] | { [key: string]: CborValue };

interface BitMaskValues {
  Batt: string;
  Rssi: string;
  Plugged: string;
  DC2: string;
  DC1: string;
}

interface PacketRow {
  bitMask: BitMaskValues;
  sensors: number[];
  timestamp: number;
  values: (number | typeof NaN)[];
}

interface ConfigField {
  key: string;
  value: string | number | boolean;
  type: string;
}

interface ParseResult {
  packetType: 'sensor_data' | 'config';
  identifier: number;
  version: number;
  payloadLength: number;
  productID: string;
  packetNumber: number | null;
  checksumValid: boolean;
  rows: PacketRow[];
  configFields?: ConfigField[];
  configJson?: Record<string, CborValue>;
  cborPayloadHex?: string;
}

// ---------------------------------------------------------------------------
// Parse logic
// ---------------------------------------------------------------------------

function buildConfigResult(packetArray: Uint8Array, length: number, version: number): ParseResult | null {
  const candidates = [
    { start: 13, productID: bytesToHex(packetArray.slice(5, 11)), packetNumber: (packetArray[11] << 8) + packetArray[12] },
    { start: 5, productID: null, packetNumber: null },
  ];

  for (const candidate of candidates) {
    if (candidate.start >= packetArray.length - 1) continue;

    try {
      const cborPayload = packetArray.slice(candidate.start, packetArray.length - 1);
      const configJson = decodeCborConfig(cborPayload);
      const configFields = Object.entries(configJson).map(([key, value]) => ({
        key,
        value: formatCborValue(value),
        type: cborType(value),
      }));

      return {
        packetType: 'config',
        identifier: 0x10,
        version,
        payloadLength: length,
        productID: candidate.productID || productIdFromConfig(configJson),
        packetNumber: candidate.packetNumber,
        checksumValid: verifyChecksum(packetArray),
        rows: [],
        configFields,
        configJson,
        cborPayloadHex: bytesToHex(cborPayload),
      };
    } catch {
      // Try the next supported framing style.
    }
  }

  return null;
}

export function parsePacket(raw: string): ParseResult | null {
  const matches = raw.replace(/\s/g, '').match(/[\da-f]{2}/gi);
  if (!matches || matches.length < 6) return null;

  const packetArray = new Uint8Array(matches.map((h) => parseInt(h, 16)));
  const length = (packetArray[1] << 8) + packetArray[2];
  const identifier = packetArray[3];
  const version = packetArray[4];

  if (length !== packetArray.length - 5 || version !== 1) return null;

  if (identifier === 0x10) return buildConfigResult(packetArray, length, version);
  if (identifier !== 0x08) return null;

  const productID = bytesToHex(packetArray.slice(5, 11));
  const packetNumber = (packetArray[11] << 8) + packetArray[12];
  const checksumValid = verifyChecksum(packetArray);

  const rows: PacketRow[] = [];
  let i = 13;
  const dataEnd = packetArray.length - 1;

  while (i < dataEnd) {
    if (i + 10 > dataEnd) return null;
    const bitMaskRaw = (packetArray[i++] << 8) + packetArray[i++];

    const sensors: number[] = [0, 0, 0, 0];
    for (let ii = 3; ii >= 0; ii--) {
      sensors[ii] = packetArray[i++];
    }

    const timestamp =
      (packetArray[i] & 0xff) * 0x1000000
      + ((packetArray[i + 1] & 0xff) << 16)
      + ((packetArray[i + 2] & 0xff) << 8)
      + (packetArray[i + 3] & 0xff);
    i += 4;

    const values: (number | typeof NaN)[] = [NaN, NaN, NaN, NaN];
    for (let ii = 0; ii < 4; ii++) {
      if (sensors[ii] !== 0) {
        if (i + 2 > dataEnd) return null;
        values[ii] = (packetArray[i++] << 8) + packetArray[i++];
      }
    }

    const bitMask: BitMaskValues = {
      Batt: (bitMaskRaw >> 7) / 100 + 'V',
      Rssi: ((bitMaskRaw >> 4) & 7).toString() + '/7',
      Plugged: ((bitMaskRaw & 8) >> 3) === 1 ? 'YES' : 'NO',
      DC2: ((bitMaskRaw & 2) >> 1) === 1 ? 'CLOSED' : 'OPEN',
      DC1: (bitMaskRaw & 1) === 1 ? 'CLOSED' : 'OPEN',
    };

    rows.push({ bitMask, sensors, timestamp, values });
  }

  return {
    packetType: 'sensor_data',
    identifier,
    version,
    payloadLength: length,
    productID,
    packetNumber,
    checksumValid,
    rows,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Packet Reference Documentation
// ---------------------------------------------------------------------------

function PacketDocs() {
  return (
    <div className="space-y-6">
      {/* Frame Structure */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Packet Frame Structure</CardTitle>
          <CardDescription>
            ATEK E5 transmitters use the same outer frame for sensor data and config packets.
            Identifier <code className="bg-muted px-1 rounded text-xs">0x08</code> carries sensor data; <code className="bg-muted px-1 rounded text-xs">0x10</code> carries CBOR config.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Bytes</TableHead>
                  <TableHead className="w-32">Field</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell><code className="text-xs">0</code></TableCell>
                  <TableCell className="font-medium">Start byte</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Always <code className="bg-muted px-1 rounded text-xs">0xAC</code></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">1-2</code></TableCell>
                  <TableCell className="font-medium">Length</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Payload length after the length field (uint16 big-endian)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">3</code></TableCell>
                  <TableCell className="font-medium">Identifier</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    Packet type: <code className="bg-muted px-1 rounded text-xs">0x08</code> = sensor data, <code className="bg-muted px-1 rounded text-xs">0x10</code> = CBOR config
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">4</code></TableCell>
                  <TableCell className="font-medium">Version</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    Protocol version: <code className="bg-muted px-1 rounded text-xs">0x01</code>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">5-10</code></TableCell>
                  <TableCell className="font-medium">Product ID</TableCell>
                  <TableCell className="text-sm text-muted-foreground">6-byte MAC address for sensor packets; config packets may start CBOR here</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">11-12</code></TableCell>
                  <TableCell className="font-medium">Packet Number</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Sequential counter for sensor packets and E5 config confirmations</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">13...</code></TableCell>
                  <TableCell className="font-medium">Payload</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Repeated sensor data blocks or CBOR config map</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">last</code></TableCell>
                  <TableCell className="font-medium">Checksum</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <code className="bg-muted px-1 rounded text-xs">255 - (sum of all preceding bytes & 0xFF)</code>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Data Record */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Data Record Structure</CardTitle>
          <CardDescription>
            Each data record contains a bitmask, 4 sensor type bytes, a Unix timestamp, and up to 4 sensor values.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Bytes</TableHead>
                  <TableHead className="w-32">Field</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell><code className="text-xs">0-1</code></TableCell>
                  <TableCell className="font-medium">Bitmask</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    16-bit status field (see bitmask table below)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">2-5</code></TableCell>
                  <TableCell className="font-medium">Sensor Types</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    4 bytes, one per channel (S3, S2, S1, S0). <code className="bg-muted px-1 rounded text-xs">0x00</code> = empty slot
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">6-9</code></TableCell>
                  <TableCell className="font-medium">Timestamp</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    Unix epoch in seconds (uint32 big-endian)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">10+</code></TableCell>
                  <TableCell className="font-medium">Values</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    2 bytes per non-zero sensor (uint16 big-endian). Only present if sensor type is not <code className="bg-muted px-1 rounded text-xs">0x00</code>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Bitmask */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bitmask Breakdown</CardTitle>
          <CardDescription>
            The 16-bit bitmask encodes battery voltage, signal strength, and digital contact states.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Bits</TableHead>
                  <TableHead className="w-32">Field</TableHead>
                  <TableHead>Formula</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell><code className="text-xs">15-7</code></TableCell>
                  <TableCell className="font-medium">Battery</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <code className="bg-muted px-1 rounded text-xs">(bitmask &gt;&gt; 7) / 100</code> volts
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">6-4</code></TableCell>
                  <TableCell className="font-medium">RSSI</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <code className="bg-muted px-1 rounded text-xs">(bitmask &gt;&gt; 4) & 0x07</code> — signal strength 0-7
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">3</code></TableCell>
                  <TableCell className="font-medium">Plugged</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <code className="bg-muted px-1 rounded text-xs">1</code> = power adapter connected
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">2</code></TableCell>
                  <TableCell className="font-medium">Reserved</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Unused</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">1</code></TableCell>
                  <TableCell className="font-medium">DC2</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    Dry contact 2 — <code className="bg-muted px-1 rounded text-xs">1</code> = closed
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">0</code></TableCell>
                  <TableCell className="font-medium">DC1</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    Dry contact 1 — <code className="bg-muted px-1 rounded text-xs">1</code> = closed
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Sensor Types */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sensor Module Types</CardTitle>
          <CardDescription>
            Each sensor byte identifies the probe hardware. The base type is the upper 5 bits (<code className="bg-muted px-1 rounded text-xs">type & 0xF8</code>); lower 3 bits are the variant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Code</TableHead>
                  <TableHead className="w-40">Module</TableHead>
                  <TableHead className="w-28">Measures</TableHead>
                  <TableHead>Value Conversion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { code: '0x10', name: 'TH (BME280)', variants: '0x11 SHT40, 0x12 SHTC3, 0x13 Dragino', measures: 'Temp/RH', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x18', name: 'CO2 (CozIR)', variants: '0x19 STC31, 0x1A CO2 Pump', measures: 'CO2 ppm', conversion: '(raw / 100 - 200) / 1000' },
                  { code: '0x20', name: 'Pressure', variants: '', measures: 'kPa', conversion: 'signed(raw) / 10' },
                  { code: '0x30', name: 'DS18B20', variants: '', measures: 'Temp', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x38', name: '4-20mA', variants: '', measures: 'mA', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x40', name: 'MAX31865 RTD', variants: '0x41 Extended range', measures: 'Temp', conversion: 'signed(raw) / 10 (ext)' },
                  { code: '0x48', name: 'Thermistor', variants: '0x49 Builtin, 0x4A Legacy', measures: 'Temp', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x50', name: 'SFM4100', variants: '', measures: 'Flow', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x60', name: 'TSL2561', variants: '', measures: 'Lux', conversion: 'raw (no conversion)' },
                  { code: '0x70', name: 'THL', variants: '', measures: 'Temp/RH/Lux', conversion: 'raw (no conversion)' },
                  { code: '0x78', name: '0-10V', variants: '', measures: 'Voltage', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x80', name: 'O2 (SST)', variants: '0x81 DFRobot', measures: 'O2 %', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0x90', name: 'Coulomb', variants: '', measures: 'Charge', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0xA0', name: 'Dry Contact', variants: '0xA1 LN2', measures: 'State', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0xB0', name: 'Balance', variants: '', measures: 'Weight', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0xC0', name: 'Pulse Counter', variants: '', measures: 'Count', conversion: 'raw (no conversion)' },
                  { code: '0xE0', name: 'SPS30', variants: '', measures: 'Particulate', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0xE8', name: 'IPS7100', variants: '', measures: 'Particle count', conversion: 'raw * 1000' },
                  { code: '0xE9', name: 'IPS7100', variants: '', measures: 'Particle count', conversion: 'raw * 100' },
                  { code: '0xEA', name: 'IPS7100', variants: '', measures: 'Particle count', conversion: 'raw * 10' },
                  { code: '0xEB', name: 'IPS7100', variants: '', measures: 'Particle count', conversion: 'raw * 1' },
                  { code: '0xF0', name: 'ITA Weather', variants: '', measures: 'Weather', conversion: '(raw / 100 - 200) / 100' },
                  { code: '0xF8', name: 'CH4 (SJH-100A)', variants: '', measures: 'Methane', conversion: '(raw / 100 - 200) / 100' },
                ].map((s) => (
                  <TableRow key={s.code}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">{s.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-sm">{s.name}</div>
                      {s.variants && (
                        <div className="text-xs text-muted-foreground mt-0.5">{s.variants}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{s.measures}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{s.conversion}</code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Packet Identifiers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Packet Identifiers</CardTitle>
          <CardDescription>
            The identifier byte (byte 3) determines the packet type and how the payload is parsed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Code</TableHead>
                  <TableHead className="w-48">Type</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { code: '0x06', desc: 'VMTL sensor data', status: 'deprecated', statusColor: 'secondary' as const },
                  { code: '0x07', desc: 'Weather station data', status: 'deprecated', statusColor: 'secondary' as const },
                  { code: '0x08', desc: 'Multi-sensor transmitter', status: 'active', statusColor: 'default' as const },
                  { code: '0x09', desc: 'Firmware info packet', status: 'active', statusColor: 'default' as const },
                  { code: '0x10', desc: 'CBOR config packet', status: 'active', statusColor: 'default' as const },
                ].map((p) => (
                  <TableRow key={p.code} className={p.status === 'active' ? '' : 'opacity-60'}>
                    <TableCell>
                      <Badge variant={p.status === 'active' ? 'outline' : 'secondary'} className="font-mono text-xs">{p.code}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{p.desc}</TableCell>
                    <TableCell>
                      <Badge variant={p.statusColor} className="text-xs">
                        {p.status === 'deprecated' ? 'Deprecated' : 'Active'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* 0x09 Firmware Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">0x09 — Firmware Info Packet</CardTitle>
          <CardDescription>
            Sent on transmitter boot. Contains the firmware version and git commit. No sensor data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Bytes</TableHead>
                  <TableHead className="w-40">Field</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell><code className="text-xs">0-5</code></TableCell>
                  <TableCell className="font-medium">Product ID</TableCell>
                  <TableCell className="text-sm text-muted-foreground">6-byte MAC address</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">6</code></TableCell>
                  <TableCell className="font-medium">Repo name length</TableCell>
                  <TableCell className="text-sm text-muted-foreground">Length of the firmware repository name string</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">7..7+N</code></TableCell>
                  <TableCell className="font-medium">Repo name</TableCell>
                  <TableCell className="text-sm text-muted-foreground">ASCII string (e.g. <code className="bg-muted px-1 rounded text-xs">atek-firmware</code>)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code className="text-xs">7+N..10+N</code></TableCell>
                  <TableCell className="font-medium">Git commit</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    uint32 — 7-char hex hash. If <code className="bg-muted px-1 rounded text-xs">&gt; 0x10000000</code>, the working tree was dirty (subtract <code className="bg-muted px-1 rounded text-xs">0x10000000</code> to get actual hash)
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-700 dark:text-blue-400">
              This packet sets <code className="bg-muted px-1 rounded">reboot = true</code> on the transmitter, signaling the platform that the device just started up.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 0x10 CBOR Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">0x10 — CBOR Config Packet</CardTitle>
          <CardDescription>
            Transmitter configuration sent as a CBOR-encoded map. Used for OTA config sync and device provisioning.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The interpreter supports the 5-byte framed config packet shown in legacy raw packets and the ATEK E5 confirmation format with product ID and packet number before the CBOR payload. The decoded CBOR map is stored as <code className="bg-muted px-1 rounded text-xs">configjson</code> on the transmitter document.
          </p>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">CBOR Key</TableHead>
                  <TableHead className="w-20">Type</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { key: 'mac', type: 'int', desc: 'Device MAC address (identifies the transmitter)' },
                  { key: 'read_hi', type: 'int', desc: 'Sensor read interval — high power mode (seconds)' },
                  { key: 'read_lo', type: 'int', desc: 'Sensor read interval — low power mode (seconds)' },
                  { key: 'send_hi', type: 'int', desc: 'Packet send interval — high power mode (seconds)' },
                  { key: 'send_lo', type: 'int', desc: 'Packet send interval — low power mode (seconds)' },
                  { key: 'store', type: 'int', desc: 'Points to buffer before sending' },
                  { key: 'power', type: 'int', desc: 'Power mode threshold' },
                  { key: 'timesync', type: 'int', desc: 'NTP time sync interval (seconds)' },
                  { key: 'moduleID', type: 'int', desc: 'Hardware module identifier' },
                  { key: 'offset_1..4', type: 'float', desc: 'Calibration offset for each sensor channel' },
                  { key: 'offset_N_a/b/c', type: 'float', desc: 'Polynomial calibration coefficients (a, b, c) per channel' },
                  { key: 'alert_hi_1..4', type: 'float', desc: 'High alert threshold per channel' },
                  { key: 'alert_lo_1..4', type: 'float', desc: 'Low alert threshold per channel' },
                  { key: 'buzzer', type: 'int', desc: 'Buzzer enable (0/1)' },
                  { key: 'connectID', type: 'int', desc: 'Connection mode identifier' },
                  { key: 'fwidHIGH', type: 'int', desc: 'Firmware ID — high word' },
                  { key: 'fwidMID', type: 'int', desc: 'Firmware ID — mid byte' },
                  { key: 'fwidLOW', type: 'int', desc: 'Firmware ID — low word' },
                  { key: 'packetNumber', type: 'int', desc: 'Current packet counter' },
                  { key: 'lastSentPacket', type: 'int', desc: 'Last successfully sent packet number' },
                  { key: 'restartReason', type: 'int', desc: 'MCU restart reason code' },
                  { key: 'wifi_ssid', type: 'string', desc: 'WiFi network name' },
                  { key: 'wifi_pass', type: 'string', desc: 'WiFi password' },
                  { key: 'wifi_mode', type: 'string', desc: 'WiFi mode (e.g. BGN)' },
                  { key: 'wifi_id', type: 'string', desc: 'WiFi identifier' },
                  { key: 'wifi_user', type: 'string', desc: 'WiFi enterprise username' },
                ].map((f) => (
                  <TableRow key={f.key}>
                    <TableCell><code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{f.key}</code></TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{f.type}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{f.desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-700 dark:text-amber-400">
              <strong>Monkey patch:</strong> Some older transmitters encode float values with incorrect CBOR type IDs, causing them to be read as very large integers.
              The platform auto-converts any <code className="bg-muted px-1 rounded">offset_*</code> or <code className="bg-muted px-1 rounded">alert_*</code> value above 10,000 or below -10,000 back to its float representation.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Example */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Example: 0x08 Multi-Sensor Packet</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 text-sm font-mono overflow-x-auto leading-relaxed">
            <code>{`AC 00 1B 08 01 34 94 54 36 75 F8 14 BA D3 0C 60 10 10 10 38 6D 99 F3 56 9D 54 C5 62 12 03 1C 63
│  │     │  │  └─────────────┘ └───┘ └──────────────────────────────────────────────────────┘
│  │     │  │   Product ID     PktNo  Data Records...
│  │     │  └── Version (0x01)
│  │     └───── Identifier (0x08 = multi-sensor)
│  └─────────── Length (0x001B = bytes after first 5 header bytes)
└────────────── Start byte (0xAC)`}</code>
          </pre>
          <p className="text-sm text-muted-foreground">
            Paste this hex string in the Decoder tab to see it parsed into header fields, bitmask values, sensor types, timestamps, and converted readings.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PacketInterpreterPage() {
  const [packetInput, setPacketInput] = useState('');
  const [result, setResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSubmit = () => {
    setParseError(null);
    const parsed = parsePacket(packetInput);
    if (!parsed) {
      setParseError('Invalid or unsupported packet. Supports version=1 ATEK E5 sensor packets (identifier=0x08) and CBOR config packets (identifier=0x10).');
      setResult(null);
    } else {
      setResult(parsed);
    }
  };

  // Populate from ?packet=<encoded-hex> on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const packetParam = params.get('packet');
    if (packetParam) {
      const decoded = decodeURIComponent(packetParam);
      setPacketInput(decoded);
      const parsed = parsePacket(decoded);
      if (parsed) setResult(parsed);
    }
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">ATEK Packet Interpreter</h1>
        <p className="text-muted-foreground mt-1">
          Decode ATEK E5 sensor packets and CBOR config packets.
        </p>
      </div>

      <Tabs defaultValue="decoder">
        <TabsList>
          <TabsTrigger value="decoder" className="gap-1.5">
            <Code2 className="h-3.5 w-3.5" />
            Decoder
          </TabsTrigger>
          <TabsTrigger value="docs" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" />
            Packet Reference
          </TabsTrigger>
        </TabsList>

        <TabsContent value="decoder" className="mt-4 space-y-6">
          {/* Input section */}
          <Card>
            <CardHeader>
              <CardTitle>Packet Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="packet-input">Hex Packet</Label>
                <Textarea
                  id="packet-input"
                  rows={4}
                  placeholder="Paste hex packet here..."
                  value={packetInput}
                  onChange={(e) => setPacketInput(e.target.value)}
                />
              </div>
              <Button onClick={handleSubmit}>Submit</Button>

              {parseError && (
                <p className="text-sm text-destructive">{parseError}</p>
              )}
            </CardContent>
          </Card>

          {/* Parsed header fields */}
          {result && (
            <Card>
              <CardHeader>
                <CardTitle>Packet Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                <div className="space-y-1">
                  <Label htmlFor="packetType">Packet Type</Label>
                  <Input
                    id="packetType"
                    value={result.packetType === 'config' ? 'CBOR config' : 'Sensor data'}
                    disabled
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="productID">ProductID</Label>
                  <Input id="productID" value={result.productID} disabled />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="packetNumber">PacketNumber</Label>
                  <Input id="packetNumber" value={result.packetNumber ?? '—'} disabled />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="datas">{result.packetType === 'config' ? 'Config Fields' : 'Data Records'}</Label>
                  <Input
                    id="datas"
                    value={result.packetType === 'config' ? (result.configFields?.length || 0) : result.rows.length}
                    disabled
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="checksum">Checksum</Label>
                  <div className="flex items-center h-9 gap-2">
                    <Checkbox id="checksum" checked={result.checksumValid} disabled />
                    <span className="text-sm text-muted-foreground">
                      {result.checksumValid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data rows table */}
          {result && result.rows.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Data Records</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead colSpan={5} className="text-center border-r">
                          Bitmask
                        </TableHead>
                        <TableHead colSpan={4} className="text-center border-r">
                          Sensors
                        </TableHead>
                        <TableHead className="border-r">Timestamp</TableHead>
                        <TableHead colSpan={4} className="text-center">
                          Values
                        </TableHead>
                      </TableRow>
                      <TableRow className="text-xs text-muted-foreground">
                        <TableHead>Batt</TableHead>
                        <TableHead>Rssi</TableHead>
                        <TableHead>Plugged</TableHead>
                        <TableHead>DC2</TableHead>
                        <TableHead className="border-r">DC1</TableHead>
                        <TableHead>S0</TableHead>
                        <TableHead>S1</TableHead>
                        <TableHead>S2</TableHead>
                        <TableHead className="border-r">S3</TableHead>
                        <TableHead className="border-r">Timestamp</TableHead>
                        <TableHead>V0</TableHead>
                        <TableHead>V1</TableHead>
                        <TableHead>V2</TableHead>
                        <TableHead>V3</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.rows.map((row, idx) => (
                        <TableRow key={idx} className="even:bg-muted/30">
                          <TableCell>{row.bitMask.Batt}</TableCell>
                          <TableCell>{row.bitMask.Rssi}</TableCell>
                          <TableCell>{row.bitMask.Plugged}</TableCell>
                          <TableCell>{row.bitMask.DC2}</TableCell>
                          <TableCell className="border-r">{row.bitMask.DC1}</TableCell>
                          {row.sensors.map((s, si) => (
                            <TableCell key={si} className={si === 3 ? 'border-r' : ''}>
                              {'0x' + s.toString(16).toUpperCase()}
                            </TableCell>
                          ))}
                          <TableCell className="border-r whitespace-nowrap">
                            {new Date(row.timestamp * 1000).toLocaleString('sv-SE')}
                          </TableCell>
                          {row.values.map((v, vi) => {
                            const converted = isNaN(v as number)
                              ? NaN
                              : convertValue(row.sensors[vi], v as number);
                            return (
                              <TableCell key={vi}>
                                {isNaN(converted) ? '' : converted.toFixed(2)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {result?.packetType === 'config' && result.configFields && (
            <Card>
              <CardHeader>
                <CardTitle>CBOR Config Fields</CardTitle>
                <CardDescription>
                  Decoded from payload <code className="bg-muted px-1 rounded text-xs">
                    {result.cborPayloadHex?.slice(0, 48)}
                    {(result.cborPayloadHex?.length || 0) > 48 ? '...' : ''}
                  </code>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Key</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.configFields.map((field) => (
                        <TableRow key={field.key}>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{field.key}</code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{field.type}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs break-all">
                            {String(field.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 text-sm font-mono overflow-x-auto leading-relaxed">
                  <code>{JSON.stringify(toJsonSafe(result.configJson || {}), null, 2)}</code>
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="docs" className="mt-4 space-y-6">
          <PacketDocs />
        </TabsContent>
      </Tabs>
    </div>
  );
}
